import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ModalComponent } from '../../../modals/modal/modal.component';
import { ProformsAdminService } from '../../../../services/proforms/proforms-admin.service';
import { UpdateSectionPayload } from '../../../../types/payloads/proforms.payloads';
import {
  ProformFieldTypes,
  ProFormsAnswerOptionAPIResponse,
  ProFormsQuestionAPIResponse,
  ProFormsSectionAPIResponse,
  ProFormType,
} from '../../../../types/responses/proforms.responses';

@Component({
  selector: 'app-proforms-admin-section',
  standalone: false,
  templateUrl: './proforms-admin-section.component.html',
  styleUrl: './proforms-admin-section.component.scss',
})
export class ProformsAdminSectionComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() section: ProFormsSectionAPIResponse;

  @Input() sectionIndex: number;

  @Input() allSections: ProFormsSectionAPIResponse[];

  @Input() formId: number;

  @Input() formType: ProFormType;

  ProFormType = ProFormType;

  @Output() readonly reorderRequested = new EventEmitter<void>();

  @Output() readonly liveTitleChange = new EventEmitter<string>();

  @ViewChild('deleteSectionModal') deleteSectionModal: ModalComponent;

  sectionTitle = '';

  sectionDescription = '';

  isConditional = false;

  isActive = false;

  selectedConditionQuestionId: number | null = null;

  selectedConditionAnswerId: number | null = null;

  conditionQuestions: ProFormsQuestionAPIResponse[] = [];

  conditionAnswers: ProFormsAnswerOptionAPIResponse[] = [];

  private titleSubject = new Subject<string>();

  private descSubject = new Subject<string>();

  private subs = new Subscription();

  constructor(private proformsAdminService: ProformsAdminService) {}

  ngOnInit() {
    this.syncFromInput();

    this.subs.add(
      this.titleSubject
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe((title) => {
          this.proformsAdminService
            .updateSection(this.section.id, { title })
            .subscribe();
        })
    );

    this.subs.add(
      this.descSubject
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe((description) => {
          this.proformsAdminService
            .updateSection(this.section.id, { description })
            .subscribe();
        })
    );

    // Rebuild condition questions whenever any question in the form changes,
    // since Object.assign mutates questions in-place without changing the
    // allSections array reference, so ngOnChanges won't detect the update.
    this.subs.add(
      this.proformsAdminService.currentProForm.subscribe(() => {
        this.buildConditionQuestions();
        if (this.selectedConditionQuestionId) {
          this.buildConditionAnswers(this.selectedConditionQuestionId);
        }
      })
    );

    this.subs.add(
      this.proformsAdminService.currentActiveSection.subscribe((id) => {
        this.isActive = id === this.section.id;
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['section'] && !changes['section'].firstChange) {
      this.syncFromInput();
    }
    if (changes['allSections']) {
      this.buildConditionQuestions();
    }
  }

  private syncFromInput() {
    this.sectionTitle = this.section.title ?? '';
    this.sectionDescription = this.section.description ?? '';
    this.isConditional = !!this.section.is_conditional;
    this.selectedConditionQuestionId =
      this.section.conditional_question_id ?? null;
    this.selectedConditionAnswerId =
      this.section.conditional_question_answer_option_id ?? null;
    this.buildConditionQuestions();
    if (this.selectedConditionQuestionId) {
      this.buildConditionAnswers(this.selectedConditionQuestionId);
    }
  }

  buildConditionQuestions() {
    if (!this.allSections) return;
    const previousSections = this.allSections.filter(
      (s) => s.sort_order < this.section.sort_order
    );
    this.conditionQuestions = previousSections.flatMap((s) =>
      s.questions.filter(
        (q) =>
          q.field_type === ProformFieldTypes.RADIO ||
          q.field_type === ProformFieldTypes.DROPDOWN
      )
    );
  }

  buildConditionAnswers(questionId: number) {
    const question = this.conditionQuestions.find((q) => q.id === questionId);
    this.conditionAnswers = question ? [...question.answerOptions] : [];
  }

  onTitleChange(value: string) {
    this.liveTitleChange.emit(value);
    this.titleSubject.next(value);
    this.proformsAdminService.patchSectionInMemory(this.section.id, {
      title: value,
    });
  }

  onDescriptionChange(value: string) {
    this.descSubject.next(value);
    this.proformsAdminService.patchSectionInMemory(this.section.id, {
      description: value,
    });
  }

  toggleConditional() {
    this.isConditional = !this.isConditional;
    const payload: UpdateSectionPayload = {
      is_conditional: this.isConditional ? 1 : 0,
    };
    if (!this.isConditional) {
      this.selectedConditionQuestionId = null;
      this.selectedConditionAnswerId = null;
      payload.conditional_question_id = null;
      payload.conditional_question_answer_option_id = null;
    }
    // Mutate in place so form.sections in the builder reflects the new state
    // immediately (used by the reorder modal and other consumers).
    this.section.is_conditional = this.isConditional ? 1 : 0;
    if (!this.isConditional) {
      this.section.conditional_question_id = null;
      this.section.conditional_question_answer_option_id = null;
    }
    this.proformsAdminService
      .updateSection(this.section.id, payload)
      .subscribe();
  }

  onConditionQuestionChange() {
    this.selectedConditionAnswerId = null;
    if (this.selectedConditionQuestionId) {
      this.buildConditionAnswers(this.selectedConditionQuestionId);
    } else {
      this.conditionAnswers = [];
    }
    this.saveCondition();
  }

  onConditionAnswerChange() {
    this.saveCondition();
  }

  saveCondition() {
    this.section.conditional_question_id = this.selectedConditionQuestionId;
    this.section.conditional_question_answer_option_id =
      this.selectedConditionAnswerId;
    this.proformsAdminService
      .updateSection(this.section.id, {
        conditional_question_id: this.selectedConditionQuestionId,
        conditional_question_answer_option_id: this.selectedConditionAnswerId,
      })
      .subscribe();
  }

  openDeleteModal() {
    this.deleteSectionModal.open();
  }

  deleteSection() {
    this.deleteSectionModal.close();
    this.proformsAdminService.deleteSectionAndRefresh(
      this.section.id,
      this.formId
    );
  }

  activate() {
    this.proformsAdminService.setActiveSection(this.section.id);
  }

  requestReorder() {
    this.reorderRequested.emit();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
